import type { IDatePickerStrings } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import moment from "moment";

export const toHumanizeDate = (date: string) => {
    const now = moment();
    const value = moment(date);
    if (now.diff(value, "month") === 0) {
        return value.fromNow();
    }

    if (now.diff(value, "year") === 0) {
        return value.format("MMMM DD");
    }

    return value.format("MMMM DD, YYYY");
};

/**
 * Returns the new Date object corresponding to the 00:00:00Z time (UTC) of the input `date``
 */
export const startOfUTCDay = (date: Date) => {
    const result = new Date(date);
    result.setUTCHours(0, 0, 0, 0);
    return result;
};

export const dateFormat = {
    LONG_DATE_FORMAT: "MMMM DD, YYYY",
    YEAR_MONTH_DAY: "YYYY-MM-DD",
    MONTH_YEAR: "MM/YYYY",
    DATE_FORMAT_WITH_TIME: "MMM. D, YYYY hh:mm:ss",
    LONG_FULL_DATE_FORMAT: "YYYY_MM_DD_hh_mm_ss_SSS",
    MONTH_DAY_YEAR: "MM/DD/YYYY",
};

export const appendDateSuffix = (value: string, includeSeconds: boolean = false) => {
    const dateFormat = includeSeconds ? "YYYY-MMM-DD HH-mm-ss" : "YYYY-MMM-DD HH-mm";
    return `${value} ${moment().format(dateFormat)}`;
};

export const getNameWithDateFormat = (name: string, format?: string) => {
    return `${name}${moment().format(format ?? dateFormat.LONG_DATE_FORMAT)}`;
};

export const getDateWithFormat = (date: string | Date, format?: string) => {
    if (!date) {
        return "--";
    }
    return moment.utc(date).format(format ?? dateFormat.LONG_DATE_FORMAT);
};

export const getDatePickerString = (): IDatePickerStrings => ({
    months: [
        formatMessage("January"),
        formatMessage("February"),
        formatMessage("March"),
        formatMessage("April"),
        formatMessage("May"),
        formatMessage("June"),
        formatMessage("July"),
        formatMessage("August"),
        formatMessage("September"),
        formatMessage("October"),
        formatMessage("November"),
        formatMessage("December"),
    ],
    shortMonths: [
        formatMessage("Jan"),
        formatMessage("Feb"),
        formatMessage("Mar"),
        formatMessage("Apr"),
        formatMessage("May"),
        formatMessage("Jun"),
        formatMessage("Jul"),
        formatMessage("Aug"),
        formatMessage("Sep"),
        formatMessage("Oct"),
        formatMessage("Nov"),
        formatMessage("Dec"),
    ],
    days: [
        formatMessage("Sunday"),
        formatMessage("Monday"),
        formatMessage("Tuesday"),
        formatMessage("Wednesday"),
        formatMessage("Thursday"),
        formatMessage("Friday"),
        formatMessage("Saturday"),
    ],
    shortDays: [
        formatMessage("S"),
        formatMessage("M"),
        formatMessage("T"),
        formatMessage("W"),
        formatMessage("T"),
        formatMessage("F"),
        formatMessage("S"),
    ],
    goToToday: formatMessage("Go to today"),
    prevMonthAriaLabel: formatMessage("Go to previous month"),
    nextMonthAriaLabel: formatMessage("Go to next month"),
    prevYearAriaLabel: formatMessage("Go to previous year"),
    nextYearAriaLabel: formatMessage("Go to next year"),
});

type UnitsOfTime = "seconds" | "minutes" | "hours" | "days";
export const computeTimeBetweenTwoDates = (startTimestamp: string, endTimestamp: string, units: UnitsOfTime) => {
    const startDate = new Date(startTimestamp);
    const endDate = new Date(endTimestamp);
    const timeElapsedBetweenDates = endDate.getTime() - startDate.getTime();

    switch (units) {
        case "seconds":
            return timeElapsedBetweenDates / 1000;
        case "minutes":
            return timeElapsedBetweenDates / (1000 * 60);
        case "hours":
            return timeElapsedBetweenDates / (1000 * 60 * 60);
        case "days":
            return timeElapsedBetweenDates / (1000 * 60 * 60 * 24);
        default:
            // This ensures that all possible cases are explicitly handled
            const exhaustiveCheck: never = units;
            throw new Error(exhaustiveCheck);
    }
};

/**
 * Tries to convert `value` to a valid date. Expects ISO date string, Date, or ticks number.
 */
export const toSafeDate = (value?: string | Date | number | null): Date | null => {
    const result = moment.utc(value, [moment.ISO_8601, "x", "X", "YYYY/MM/DD"]); // handle WO dates
    return result?.isValid() ? result.startOf("d").toDate() : null;
};

export const alignToNow = (value?: string | Date | number | null): Date | null => {
    const now = moment.utc().startOf("d").toDate();
    const date = toSafeDate(value);
    return date < now ? now : date;
};

export const getStartOfDate = (date: Date | string | number) => moment.utc(date)?.startOf("day")?.valueOf();
